/**
 * Security utilities for HTML escaping and sanitization
 * Prevents XSS attacks by escaping user-controlled content
 */

/**
 * Escape HTML special characters to prevent XSS
 *
 * @param unsafe - Potentially unsafe string from user input or external data
 * @returns HTML-escaped safe string
 *
 * @example
 * escapeHtml("<script>alert('XSS')</script>")
 * // Returns: "&lt;script&gt;alert('XSS')&lt;/script&gt;"
 */
export function escapeHtml(unsafe: string): string {
  if (!unsafe) return '';
  return String(unsafe)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Render text content safely while preserving newlines as <br> tags
 *
 * @param text - Text content to render
 * @returns HTML-safe string with newlines converted to <br>
 *
 * @example
 * renderTextContent("Hello\nWorld")
 * // Returns: "Hello<br>World"
 */
export function renderTextContent(text: string): string {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

/**
 * Sanitize HTML content to prevent XSS while preserving safe formatting
 * Allows common email formatting tags but strips dangerous elements
 *
 * @param html - Raw HTML content from email templates
 * @returns Sanitized HTML safe for rendering
 *
 * @example
 * sanitizeHtml("<script>alert('XSS')</script><p>Hello</p>")
 * // Returns: "<p>Hello</p>"
 */
export function sanitizeHtml(html: string): string {
  if (!html) return '';

  const allowedTags = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'a',
    'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'span', 'div', 'blockquote', 'code', 'pre'
  ];
  const allowedAttrs = ['href', 'style', 'class', 'target', 'rel'];

  return html
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers (onclick, onload, etc.)
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove javascript: protocol
    .replace(/javascript:/gi, '')
    // Remove data: protocol (can be used for XSS)
    .replace(/data:text\/html/gi, '')
    // Filter tags and attributes
    .replace(/<(\/?)([\w]+)([^>]*)>/g, (_match, slash, tag, attrs) => {
      const tagLower = tag.toLowerCase();

      // Remove disallowed tags entirely
      if (!allowedTags.includes(tagLower)) {
        return '';
      }

      // Clean attributes
      const cleanAttrs = attrs.replace(/(\w+)\s*=\s*["']([^"']+)["']/g, (_m: string, attr: string, value: string) => {
        const attrLower = attr.toLowerCase();

        // Only keep allowed attributes
        if (!allowedAttrs.includes(attrLower)) {
          return '';
        }

        // Extra validation for href attributes
        if (attrLower === 'href') {
          // Only allow http(s) and mailto protocols
          if (!/^(https?:|mailto:)/i.test(value)) {
            return '';
          }
        }

        // Extra validation for style attributes
        if (attrLower === 'style') {
          // Remove any expressions or imports
          if (/expression|import|@/i.test(value)) {
            return '';
          }
        }

        return ` ${attr}="${value}"`;
      });

      return `<${slash}${tag}${cleanAttrs}>`;
    });
}

/**
 * Escape template variable names for safe HTML rendering
 * Used specifically for displaying missing/placeholder variables in email previews
 *
 * @param varToken - Variable token (e.g., "{{firstName}}")
 * @returns HTML-escaped safe string
 *
 * @example
 * escapeVariableToken("{{<script>alert(1)</script>}}")
 * // Returns: "{{&lt;script&gt;alert(1)&lt;/script&gt;}}"
 */
export function escapeVariableToken(varToken: string): string {
  return escapeHtml(varToken);
}

/**
 * Sanitize user data before using in variable substitution
 * Prevents XSS if user-controlled data contains malicious HTML/JS
 *
 * @param data - Record of variable names to values from lead data
 * @returns Sanitized record with escaped values
 *
 * @example
 * sanitizeVariableData({ name: "<script>alert(1)</script>" })
 * // Returns: { name: "&lt;script&gt;alert(1)&lt;/script&gt;" }
 */
export function sanitizeVariableData(data: Record<string, unknown>): Record<string, string> {
  const sanitized: Record<string, string> = {};

  for (const [key, value] of Object.entries(data)) {
    // Only process string values
    if (typeof value === 'string') {
      sanitized[key] = escapeHtml(value);
    } else if (value !== null && value !== undefined) {
      // Convert non-string values to string and escape
      sanitized[key] = escapeHtml(String(value));
    } else {
      sanitized[key] = '';
    }
  }

  return sanitized;
}

/**
 * Strip HTML tags from content and convert to plain text.
 * Handles common HTML elements with proper spacing.
 *
 * @param html - HTML content to convert
 * @returns Plain text with HTML tags removed
 *
 * @example
 * stripHtmlToText("<p>Hello</p><p>World</p>")
 * // Returns: "Hello World"
 *
 * stripHtmlToText("<div>Line 1</div><br><div>Line 2</div>")
 * // Returns: "Line 1 Line 2"
 */
export function stripHtmlToText(html: string): string {
  if (!html) return '';

  return html
    .replace(/<\/p>/gi, ' ')      // Add space after closing </p>
    .replace(/<br\s*\/?>/gi, ' ') // Add space for <br> tags
    .replace(/<\/div>/gi, ' ')    // Add space after closing </div>
    .replace(/<[^>]*>/g, '')      // Strip all remaining HTML tags
    .replace(/&nbsp;/g, ' ')      // Convert &nbsp; to space
    .replace(/&amp;/g, '&')       // Convert &amp; to &
    .replace(/&lt;/g, '<')        // Convert &lt; to <
    .replace(/&gt;/g, '>')        // Convert &gt; to >
    .replace(/&quot;/g, '"')      // Convert &quot; to "
    .replace(/&#039;/g, "'")      // Convert &#039; to '
    .replace(/\s+/g, ' ')         // Normalize multiple spaces to single
    .trim();
}

/**
 * Convert HTML to plain text while preserving paragraph/line breaks.
 * Use for multi-paragraph content (email bodies); use stripHtmlToText for
 * single-line previews.
 *
 * NOTE: output is plain text for rendering in a text node only. It decodes
 * entities and is NOT safe to inject via dangerouslySetInnerHTML.
 *
 * @param html - HTML content to convert
 * @returns Plain text with tags removed and block boundaries as newlines
 *
 * @example
 * stripHtmlToTextWithBreaks("<div>Hi,</div><div><br /></div><div>Body</div>")
 * // Returns: "Hi,\n\nBody"
 */
export function stripHtmlToTextWithBreaks(html: string): string {
  if (!html) return '';

  return html
    // Drop whole subtrees whose TEXT CONTENT is not prose — real email HTML
    // (Outlook especially) embeds <style> CSS in the body, and tag-stripping
    // alone would leak ".MsoNormal{margin:0}…" into the text projection.
    .replace(/<(style|script|head)\b[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div)>/gi, '\n')
    .replace(/<[^>]*>/g, '') // strip remaining tags BEFORE splitting — [^>] matches across newlines, split fragments wouldn't
    .split('\n')
    .map((line) => stripHtmlToText(line))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Plain text for a model rewrite: an anchor becomes "text (url)" so a link the
 * client inserted survives the round trip.
 */
export function stripHtmlToTextWithLinks(html: string): string {
  const withUrls = html.replace(
    /<a\b[^>]*\bhref=(["'])(.*?)\1[^>]*>([\s\S]*?)<\/a>/gi,
    (_, _quote: string, href: string, text: string) =>
      text.trim() === href ? href : `${text} (${href})`,
  );
  return stripHtmlToTextWithBreaks(withUrls);
}

const WRAPPING_QUOTES = /^\s*(?:"([^"]*)"|“([^”]*)”)\s*$/;

/** A model's reply without a quote pair it wrapped around the whole text. */
export function stripWrappingQuotes(text: string): string {
  const match = WRAPPING_QUOTES.exec(text);
  return (match ? (match[1] ?? match[2]) : text).trim();
}

/**
 * Structural quoted-history / signature-block boundaries — always safe to cut at,
 * wherever they appear. Matched against a full trimmed line.
 */
const STRUCTURAL_CUT_MARKERS = [
  /^-{2,}\s*original message\s*-{2,}$/i,
  /^from:\s/i,
  /^sent:\s/i,
  /^subject:\s/i,
  /^to:\s/i,
  /^sent from my /i,
  /^on .{1,200} wrote:$/i,
  /^disclaimer[:\s]/i,
  /^--$/,
];

/**
 * Conversational sign-offs (own line only — "…not interested. Thanks." inside a
 * sentence must never trigger). Only a cut point when what FOLLOWS looks like a
 * signature (see isSignatureTail): a bare "Thanks!" mid-message is content.
 */
const SIGNOFF_MARKER =
  /^(regards|best regards|kind regards|warm regards|best|thanks|thank you|cheers)[,.!]?$/i;

/**
 * Everything after a sign-off reads as a signature when it runs into a
 * structural marker (disclaimer / quoted header bounds the block), or every
 * remaining line is short contact-info-ish text with no question in it.
 */
function isSignatureTail(lines: string[], signoffIndex: number): boolean {
  const rest = lines.slice(signoffIndex + 1).map((l) => l.trim()).filter(Boolean);
  if (rest.some((l) => STRUCTURAL_CUT_MARKERS.some((m) => m.test(l)))) return true;
  return rest.every((l) => l.length <= 80 && !l.endsWith('?'));
}

/**
 * Trim an email reply body (plain text, newline-separated) down to the human
 * message: cut everything from the first structural marker on, or from a
 * sign-off line when it sits in the message's tail. Returns the input unchanged
 * when no marker is found or the cut would leave nothing. The stored body stays
 * raw — this is a render-time cleanup.
 *
 * @example
 * trimQuotedReply("Sounds good!\n\nRegards\n\nDave Vick\nSVP…")
 * // Returns: "Sounds good!"
 */
export function trimQuotedReply(text: string): string {
  const lines = text.split('\n');
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    const structural = STRUCTURAL_CUT_MARKERS.some((marker) => marker.test(line));
    const signoff = SIGNOFF_MARKER.test(line) && isSignatureTail(lines, i);
    if (structural || signoff) {
      const head = lines.slice(0, i).join('\n').trim();
      if (head) return head;
    }
  }
  return text.trim();
}

/**
 * Get a preview of HTML content, stripped and truncated.
 *
 * @param html - HTML content
 * @param maxLength - Maximum length of preview (default: 60)
 * @returns Truncated plain text preview
 *
 * @example
 * getTextPreview("<p>This is a long paragraph...</p>", 20)
 * // Returns: "This is a long parag..."
 */
export function getTextPreview(html: string, maxLength: number = 60): string {
  const text = stripHtmlToText(html);
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Escape special regex characters in a string
 * Prevents regex injection when building dynamic RegExp patterns
 *
 * @param str - String to escape for use in RegExp
 * @returns String with regex special characters escaped
 *
 * @example
 * escapeRegex("hello (world)")
 * // Returns: "hello \\(world\\)"
 */
export function escapeRegex(str: string): string {
  if (!str) return '';
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Simple markdown formatter for chat messages
 * Handles basic formatting: bold, italic, and inline code
 * Escapes HTML first to prevent XSS attacks
 *
 * @param text - Plain text with markdown syntax
 * @returns HTML-safe string with markdown converted to HTML tags
 *
 * @example
 * formatMarkdown("Hello **world**")
 * // Returns: "Hello <strong>world</strong>"
 */
export function formatMarkdown(text: string): string {
  if (!text) return '';

  // Escape HTML first to prevent XSS
  const escaped = escapeHtml(text);

  // Process code spans FIRST and protect their content from emphasis processing
  // Use placeholders to temporarily store code blocks
  const codeBlocks: string[] = [];
  const PLACEHOLDER_PREFIX = '___CODE_BLOCK_';
  const PLACEHOLDER_SUFFIX = '___';
  const withCodePlaceholders = escaped.replace(/`([^`]+)`/g, (_match, code) => {
    const index = codeBlocks.length;
    codeBlocks.push(code);
    return `${PLACEHOLDER_PREFIX}${index}${PLACEHOLDER_SUFFIX}`;
  });

  // Apply emphasis formatting (code content is now protected by placeholders)
  const withEmphasis = withCodePlaceholders
    // Bold: **text**
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    // Italic: *text* (but not inside bold)
    .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');

  // Handle markdown links: [text](url) - only allow relative URLs or https
  // Styled as buttons for better UX
  const withLinks = withEmphasis.replace(
    /\[([^\]]+)\]\(((\/[^\s)]+)|(https:\/\/[^\s)]+))\)/g,
    '<a href="$2" class="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 text-[12px] font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors no-underline">$1</a>'
  );

  // Restore code blocks with proper HTML
  const placeholderRegex = new RegExp(`${PLACEHOLDER_PREFIX}(\\d+)${PLACEHOLDER_SUFFIX}`, 'g');
  return withLinks.replace(placeholderRegex, (_match, index) => {
    const code = codeBlocks[parseInt(index, 10)];
    return `<code class="px-1.5 py-0.5 bg-gray-100 rounded text-[13px] font-mono">${code}</code>`;
  });
}

/**
 * Allow-list a URL for use as a rendered href (SKY-513 review P1): only
 * http(s) survives; anything else (javascript:, data:, relative garbage from
 * CSV import / enrichment) becomes null and the link simply doesn't render.
 */
export const safeHttpUrl = (url: string | null | undefined): string | null =>
  url && /^https?:\/\//i.test(url) ? url : null;
